import React, { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import gql from 'graphql-tag';

import Journal from '~Utils/journal';

import HistoryView from './view';
import {
  getSelectedOrder,
  getSelectedOrder_order as selectedOrder,
} from './__queries__/getSelectedOrder';
import { RouteComponentProps } from 'react-router-dom';
import { Cheque } from '@baemingo/table-events/dist/types';
import { useLogger, useSelector } from '~Hooks';

const userFragment = gql`
  fragment UserFragment on User {
    id
    role
    venues {
      id
    }
    name
    email
    phone
    company {
      id
      hasMembershipCards
    }
    membershipLevel {
      id
      title {
        id
        en
        se
        no
        es
      }
    }
    membershipCard {
      membershipLevel {
        id
        title {
          id
          en
          se
          no
          es
        }
      }
    }
  }
`;

const orderFragment = gql`
  fragment OrderFragment on Order {
    id
    easyId
    createdAt
    status
    origin
    basketToken
    hasPrintedReceiptCopy
    complianceId
    payment {
      id
      amount
      tip
      correctedTip
      type
      createdAt
      transactionId
      status
      metadata {
        cardDetails {
          cardType
          cardPaymentEntryMode
          tsi
          tvr
          aid
          maskedPan
        }
      }
      subPayments {
        id
        createdAt
        updatedAt
        amount
        status
        type
        transactionId
        correctedTip
        tip
      }
    }
    items {
      id
      isRefunded
      fraction
      message
      entry {
        id
        item {
          id
          vatRate
          categories {
            id
            name
          }
          titleLang {
            id
            se
            en
            es
            no
          }

          descriptionLang {
            id
            se
            en
            es
            no
          }
        }
      }
      price {
        id
        percentageVAT
        amount
        discount
      }
      variations {
        id
        name
        nameLang {
          id
          se
          en
          es
          no
        }
        price
      }
      removedVariations {
        id
        name
        nameLang {
          id
          se
          en
          es
          no
        }
      }
    }
    type
    statusHistory
    isTableOrder
    staff {
      id
    }
    tableSession: table {
      id
      waiterId
      staff {
        id
        name
      }
      covers
      checkId
      table {
        id
        code
      }
    }
  }
`;

const GET_SELECTED_ORDER = gql`
  query getSelectedOrder($id: ID!) {
    order: Order(id: $id) {
      ...OrderFragment
      user {
        ...UserFragment
      }
    }
  }

  ${orderFragment}
  ${userFragment}
`;

const GET_POS_DEVICE = gql`
  query getPOSDevice($id: ID!) {
    POSDevice(id: $id) {
      id
      name
    }
  }
`;
export interface HistoryOrder extends selectedOrder {
  vat: number;
  tip: number;
  posDevice?: any;
}
interface MatchParams {
  id: string;
}

type Props = RouteComponentProps<MatchParams>;

const History = ({ match }: Props) => {
  const logger = useLogger('old-history');
  const companyId = useSelector((state) => state.company);
  const [selectedCheque, setSelectedCheque] = useState<Cheque>();
  const [selectedOrder, setSelectedOrder] = useState<HistoryOrder | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  useEffect(() => {
    Journal.write('History Page Loaded');
    logger.info('History page loaded');
  }, []);

  const [getSelectedOrder, getSelectedOrderResult] = useLazyQuery<getSelectedOrder>(
    GET_SELECTED_ORDER,
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [getPOSDevice, getPOSDeviceResult] = useLazyQuery(GET_POS_DEVICE);

  useEffect(() => {
    if (selectedOrderId === null) {
      return;
    }

    getSelectedOrder({ variables: { id: selectedOrderId } });
  }, [selectedOrderId]);

  useEffect(() => {
    // loaded data processing for display selected order
    if (!getSelectedOrderResult) {
      return;
    }

    const { called, loading, data, error } = getSelectedOrderResult;

    if (!called || loading || error || !data || !data.order) {
      return;
    }

    const newSelectedOrder = data.order;

    const vat = newSelectedOrder.items.reduce((acc, item) => {
      const gross = item.price.amount - item.price.discount;
      const net = gross / (1 + item.price.percentageVAT / 100);

      return acc + (gross - net);
    }, 0);

    const roundedVat = Math.round(vat * 100) / 100;

    if (newSelectedOrder.basketToken) {
      const posDeviceId = newSelectedOrder.basketToken.split('-')[0];
      getPOSDevice({ variables: { id: posDeviceId } });
    }

    setSelectedOrder({ ...newSelectedOrder, tip: 0, vat: roundedVat });
  }, [getSelectedOrderResult]);

  useEffect(() => {
    const posDevice = getPOSDeviceResult?.data?.POSDevice;
    if (posDevice && selectedOrder) {
      setSelectedOrder({
        ...selectedOrder,
        posDevice,
      });
    }
  }, [getPOSDeviceResult]);

  return (
    <HistoryView
      venueId={match.params.id}
      companyId={companyId}
      setSelectedCheque={setSelectedCheque}
      selectedCheque={selectedCheque}
      selectedOrder={selectedOrder}
      selectedOrderId={selectedOrderId}
      setSelectedOrderId={setSelectedOrderId}
    />
  );
};

export default History;
